/**
 * Obtiene las coordenadas (lon, lat) de una parcela catastral a partir de su referencia catastral.
 * @param refcat - Referencia catastral de la parcela.
 * @returns Coordenadas (lon, lat) o null si no se encuentran.
 */

export async function getCoordinates(refcat: string) {
  const url = `https://ovc.catastro.meh.es/OVCServWeb/OVCWcfCallejero/COVCCoordenadas.svc/json/Consulta_CPMRC?SRS=EPSG:4326&RefCat=${refcat}`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    
    const data = await response.json();
    const coordinates = data.Consulta_CPMRCResult.coordenadas.coord[0];
    
    if (coordinates) {
      const lon = parseFloat(coordinates.geo.xcen);
      const lat = parseFloat(coordinates.geo.ycen);
      return { lon, lat };
    } else {
      throw new Error('No coordinates found');
    }
  } catch (error) {
      console.error('Failed to fetch coordinates:', error);
    return null;
  }
}



/**
 * Obtiene el GeoJSON de una parcela catastral usando el servicio WFS del Catastro de España.
 * @param refcat - Referencia catastral de la parcela.
 * @returns Promise<GeoJSON> - GeoJSON de la parcela.
 */
export async function getParcelGeoJSON(refcat: string): Promise<any> {
  const wfsUrl = 'https://ovc.catastro.meh.es/INSPIRE/wfsCP.aspx';
  const params = {
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    STOREDQUERY_ID: 'GetParcel',
    refcat: String(refcat),
    srsname: 'EPSG:4326'
  };

  const url = `${wfsUrl}?${new URLSearchParams(params).toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    
    const gmlText = await response.text();
    //console.log('GML recibido:', gmlText);
    
    // Intentar leer el GML manualmente y generar GeoJSON
    const manualGeojson = parseGML(gmlText);
    
    if (manualGeojson) {
      // console.log("GeoJSON generado:", manualGeojson);
      return manualGeojson;
    } else {
      throw new Error('No se pudo generar GeoJSON a partir del GML.');
    }
  } catch (error) {
    console.error('Error al obtener la parcela:', error);
    throw error;
  }
}



// Función para leer geometrías desde el GML
/**
 * Lee manualmente las coordenadas de un GML (posList) y genera un GeoJSON.
 * @param gmlText - Texto GML recibido del servicio WFS.
 * @returns GeoJSON generado manualmente.
 */
function parseGML(gmlText: string) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(gmlText, "text/xml");

  const posList = xmlDoc.querySelectorAll('gml\\:posList, posList');
  const geometries: any[] = [];

  posList.forEach(pos => {
    const coords = pos.textContent?.trim().split(" ").map(Number);
    const coordinates: number[][] = [];

    if (coords) {
      for (let i = 0; i < coords.length; i += 2) {
        coordinates.push([coords[i + 1], coords[i]]);
      }
      geometries.push(coordinates);
    }
  });

  const geojson = {
    type: "FeatureCollection",
    features: geometries.map(coords => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coords]
      },
      properties: {}
    }))
  };
  // console.log("GeoJSON:", JSON.stringify(geojson, null, 2));
  return geojson;
}




/**
   * Calcula el bounding box (BBOX) de un GeoJSON.
   * El BBOX se devuelve en formato [minLon, minLat, maxLon, maxLat].
   * Soporta diferentes tipos de geometrías como Point, LineString, Polygon, MultiPoint, MultiLineString y MultiPolygon.
   * 
   * @param {Object} geojson - Objeto GeoJSON en formato FeatureCollection.
   * @returns {Array<number>} Bounding box en formato [minLon, minLat, maxLon, maxLat].
   * 
   * @example
   * const geojsonDePrueba = {
   *   "type": "FeatureCollection",
   *   "features": [
   *     {
   *       "type": "Feature",
   *       "geometry": {
   *         "type": "Polygon",
   *         "coordinates": [
   *           [
   *             [-105.05, 39.56],
   *             [-105.03, 39.56],
   *             [-105.03, 39.58],
   *             [-105.05, 39.58],
   *             [-105.05, 39.56]
   *           ]
   *         ]
   *       }
   *     }
   *   ]
   * };
   * const bbox = calcularBoundingBox(geojsonDePrueba);
   * console.log(bbox); // [-105.05, 39.56, -105.03, 39.58]
   */
export function calcularBoundingBox(geojson: { features: any[]; } | null) {
  let minLat = Infinity, minLon = Infinity;
  let maxLat = -Infinity, maxLon = -Infinity;

  // Recorrer todas las features del GeoJSON
  geojson?.features.forEach(feature => {
    const coords = feature.geometry.coordinates;
    const tipo = feature.geometry.type;

    // Manejar diferentes tipos de geometría
    if (tipo === 'Point') {
      actualizarBBox(coords);
    } else if (tipo === 'LineString' || tipo === 'MultiPoint') {
      coords.forEach((coord: any) => actualizarBBox(coord));
    } else if (tipo === 'Polygon' || tipo === 'MultiLineString') {
      coords.forEach((ring: any[]) => {
        ring.forEach(coords => actualizarBBox(coords));
      });
    } else if (tipo === 'MultiPolygon') {
      coords.forEach((polygon: any[]) => {
        polygon.forEach(ring => {
          ring.forEach((coords: any) => actualizarBBox(coords));
        });
      });
    }
  });

  function actualizarBBox(coord: [any, any]) {
    const [lon, lat] = coord;
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }

  return [minLon, minLat, maxLon, maxLat];
}
